import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { VideoConfig, VideoConfigInput, VideoWindow } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X } from 'lucide-react';

const VIDEO_SERVICES = ['SW', 'RB', 'BB', 'AB'];

export default function VideoPage() {
	const params = useParams<{ service?: string }>();
	const navigate = useNavigate();
	const services = useQuery({ queryKey: ['services'], queryFn: api.services });

	const activeService = useMemo(() => {
		if (params.service) return params.service;
		return services.data?.find((s) => s.enabled && VIDEO_SERVICES.includes(s.code))?.code ?? 'SW';
	}, [params.service, services.data]);

	useEffect(() => {
		if (!params.service && activeService) navigate(`/video/${activeService}`, { replace: true });
	}, [params.service, activeService, navigate]);

	const tabs = (services.data ?? []).filter((s) => VIDEO_SERVICES.includes(s.code));

	return (
		<div className="flex flex-col gap-4">
			{tabs.length > 0 && (
				<Tabs value={activeService} onValueChange={(v) => navigate(`/video/${v}`)}>
					<TabsList>
						{tabs.map((s) => (
							<TabsTrigger key={s.code} value={s.code} disabled={!s.enabled}>
								{s.label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			)}
			<VideoEditor service={activeService} key={activeService} />
		</div>
	);
}

type Toast = { kind: 'ok' | 'err'; message: string } | null;

// Slug kategorii/tagu (taki jak w WordPressie), bez URL-i i ścieżek.
function toSlug(input: string): string {
	return input.trim().toLowerCase().replace(/^\/+|\/+$/g, '');
}

function ExclusionList({
	label,
	hint,
	placeholder,
	items,
	normalize,
	onChange,
}: {
	label: string;
	hint: string;
	placeholder: string;
	items: string[];
	normalize: (input: string) => string;
	onChange: (items: string[]) => void;
}) {
	const [value, setValue] = useState('');
	const add = () => {
		// Wklejenie kilku adresów naraz (po przecinku / w liniach) też działa.
		const next = value
			.split(/[\n,]+/)
			.map(normalize)
			.filter(Boolean)
			.filter((v) => !items.includes(v));
		if (next.length) onChange([...items, ...next]);
		setValue('');
	};
	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			add();
		}
	};
	const canAdd = !!normalize(value);
	return (
		<div className="flex flex-col gap-2">
			<Label>{label}</Label>
			<div className="flex gap-2">
				<Input
					value={value}
					onChange={(e) => setValue(e.target.value)}
					onKeyDown={onKeyDown}
					placeholder={placeholder}
					className="font-mono text-xs"
				/>
				<Button size="sm" variant="outline" onClick={add} disabled={!canAdd}>
					Dodaj
				</Button>
			</div>
			<p className="text-xs text-muted-foreground">
				{hint}
			</p>
			{items.length === 0 ? (
				<p className="text-xs text-muted-foreground italic">Brak wykluczeń.</p>
			) : (
				<ul className="flex flex-col gap-1">
					{items.map((item) => (
						<li key={item} className="flex items-center justify-between gap-2 rounded-md border px-2 py-1 font-mono text-xs">
							<span className="truncate" title={item}>
								{item}
							</span>
							<button
								type="button"
								aria-label={`Usuń ${item}`}
								className="text-muted-foreground hover:text-destructive"
								onClick={() => onChange(items.filter((i) => i !== item))}
							>
								<X className="size-3.5" />
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

function defaultDraft(): VideoConfigInput {
	return {
		enabled: true,
		scriptSrc: '',
		position: 6,
		disabledWindows: [],
		excludedCategories: [],
		excludedTags: [],
		version: 0,
	};
}

function VideoEditor({ service }: { service: string }) {
	const queryClient = useQueryClient();
	const [toast, setToast] = useState<Toast>(null);
	const stored = useQuery({ queryKey: ['video', service], queryFn: () => api.video.get(service) });

	const [draft, setDraft] = useState<VideoConfigInput | null>(null);
	const load = (cfg: VideoConfig | null) => setDraft({ ...(cfg ?? defaultDraft()) });
	useEffect(() => {
		if (stored.isSuccess && draft === null) load(stored.data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stored.isSuccess]);

	const payload = (): VideoConfigInput => draft ?? defaultDraft();

	const save = useMutation({
		mutationFn: () => api.video.put(service, payload()),
		onSuccess: (saved) => {
			queryClient.setQueryData(['video', service], saved);
			load(saved);
			setToast({ kind: 'ok', message: `Zapisano (v${saved.version}). Propagacja na frontendy ~1–2 min.` });
		},
		onError: (e) => {
			if (e instanceof ApiError && e.status === 409) {
				setToast({ kind: 'err', message: 'Ktoś zapisał w międzyczasie — odśwież stronę i wprowadź zmiany ponownie.' });
				return;
			}
			if (e instanceof ApiError && e.status === 422) {
				const issues = (e.body as { issues?: { path: (string | number)[]; message: string }[] })?.issues ?? [];
				setToast({ kind: 'err', message: 'Błąd walidacji: ' + issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') });
				return;
			}
			setToast({ kind: 'err', message: (e as Error).message });
		},
	});

	if (stored.isError) return <p className="text-sm text-destructive">Błąd: {String(stored.error)}</p>;
	if (!draft) return <p className="text-sm text-muted-foreground">Ładowanie…</p>;

	const set = <K extends keyof VideoConfigInput>(key: K, value: VideoConfigInput[K]) =>
		setDraft({ ...draft, [key]: value });
	const setWindow = (i: number, patch: Partial<VideoWindow>) =>
		set(
			'disabledWindows',
			draft.disabledWindows.map((w, j) => (j === i ? { ...w, ...patch } : w)),
		);

	return (
		<div className="flex flex-col gap-4">
			{toast && (
				<div
					className={
						'rounded-md border px-3 py-2 text-sm ' +
						(toast.kind === 'ok'
							? 'border-emerald-200 bg-emerald-50 text-emerald-700'
							: 'border-destructive/30 bg-destructive/10 text-destructive')
					}
				>
					{toast.message}
				</div>
			)}

			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="text-xs text-muted-foreground">
					{stored.data ? (
						<>
							v{stored.data.version} · {stored.data.updatedBy} · {new Date(stored.data.updatedAt).toLocaleString('pl-PL')}
						</>
					) : (
						<>Serwis niezainicjalizowany — frontend używa SID z env. Wklej skrypt i zapisz, żeby przejąć sterowanie.</>
					)}
				</div>
				<Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
					{save.isPending ? 'Zapisywanie…' : 'Zapisz'}
				</Button>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Globalny player</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					<div className="flex items-center gap-3">
						<Switch checked={draft.enabled} onCheckedChange={(v) => set('enabled', v)} id="enabled" />
						<Label htmlFor="enabled">Włączony</Label>
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="scriptSrc">Skrypt OnNetwork (URL lub cały tag &lt;script&gt;)</Label>
						<Input
							id="scriptSrc"
							value={draft.scriptSrc}
							onChange={(e) => set('scriptSrc', e.target.value)}
							placeholder="https://video.onnetwork.tv/embed.php?sid=…"
							className="font-mono text-xs"
						/>
						<p className="text-xs text-muted-foreground">Przy zapisie dokładany jest parametr `ext=spidersweb` (wymagany przez integrację).</p>
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="position">Pozycja: po którym bloku treści (0 = po pierwszym akapicie po leadzie)</Label>
						<Input
							id="position"
							type="number"
							min={0}
							max={50}
							value={draft.position}
							onChange={(e) => set('position', Number(e.target.value))}
							className="w-28"
						/>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Godziny wyłączenia (Europe/Warsaw)</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-2">
					{draft.disabledWindows.map((w, i) => (
						<div key={i} className="flex items-center gap-2">
							<Input type="time" value={w.start} onChange={(e) => setWindow(i, { start: e.target.value })} className="w-32" />
							<span className="text-sm text-muted-foreground">–</span>
							<Input type="time" value={w.end} onChange={(e) => setWindow(i, { end: e.target.value })} className="w-32" />
							{w.end < w.start && <span className="text-xs text-muted-foreground">przez północ</span>}
							<span className="ml-2 text-xs text-muted-foreground">w dniach</span>
							<Input
								type="date"
								value={w.from ?? ''}
								onChange={(e) => setWindow(i, { from: e.target.value || undefined })}
								className="w-40"
							/>
							<span className="text-sm text-muted-foreground">–</span>
							<Input
								type="date"
								value={w.to ?? ''}
								onChange={(e) => setWindow(i, { to: e.target.value || undefined })}
								className="w-40"
							/>
							<Button
								size="sm"
								variant="ghost"
								onClick={() => set('disabledWindows', draft.disabledWindows.filter((_, j) => j !== i))}
							>
								Usuń
							</Button>
						</div>
					))}
					<div>
						<Button
							size="sm"
							variant="outline"
							onClick={() => set('disabledWindows', [...draft.disabledWindows, { start: '22:00', end: '06:00' }])}
						>
							Dodaj przedział
						</Button>
					</div>
					<p className="text-xs text-muted-foreground">
						Przedział obejmuje start, nie obejmuje końca. Puste daty = codziennie; daty ograniczają okno do
						zakresu (włącznie). Cały dzień = 00:00–23:59. Do wyłączenia bezterminowego użyj przełącznika „Włączony”.
					</p>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Wykluczenia globalnego playera</CardTitle>
				</CardHeader>
				<CardContent className="grid gap-6 md:grid-cols-2">
					<ExclusionList
						label="Kategorie"
						hint="Slug kategorii z WordPressa, np. tech."
						placeholder="targi"
						items={draft.excludedCategories}
						normalize={toSlug}
						onChange={(v) => set('excludedCategories', v)}
					/>
					<ExclusionList
						label="Tagi"
						hint="Slug tagu z WordPressa, np. samsung."
						placeholder="samsung"
						items={draft.excludedTags}
						normalize={toSlug}
						onChange={(v) => set('excludedTags', v)}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
